import { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Ban } from 'lucide-react';

interface BanVisitorDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    visitorId?: string;
    ipAddress?: string;
    onConfirm: (reason: string) => void;
    loading?: boolean;
}

export default function BanVisitorDialog({
    open,
    onOpenChange,
    visitorId,
    ipAddress,
    onConfirm,
    loading = false
}: BanVisitorDialogProps) {
    const [reason, setReason] = useState('');

    const handleConfirm = () => {
        onConfirm(reason);
        setReason(''); // Reset form
    };

    const handleCancel = () => {
        onOpenChange(false);
        setReason(''); // Reset form
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-red-600">
                        <Ban className="h-5 w-5" />
                        Ban Visitor
                    </DialogTitle>
                    <DialogDescription>
                        This will permanently ban this visitor from accessing your chat widget. 
                        They will not be able to start new conversations.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="visitor-id">Visitor ID</Label>
                        <Input
                            id="visitor-id"
                            value={visitorId || 'Not available'}
                            readOnly
                            className="bg-muted"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="ip-address">IP Address</Label>
                        <Input
                            id="ip-address"
                            value={ipAddress || 'Not available'}
                            readOnly
                            className="bg-muted"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="reason">Reason (Optional)</Label>
                        <Textarea
                            id="reason"
                            placeholder="Enter the reason for banning this visitor..."
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className="min-h-[80px]"
                            maxLength={500}
                        />
                        <p className="text-xs text-muted-foreground">
                            {reason.length}/500 characters
                        </p>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={handleCancel}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleConfirm}
                        disabled={loading}
                    >
                        {loading ? 'Banning...' : 'Ban Visitor'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
} 